import { Slot } from "@radix-ui/react-slot";
import { ChevronRight, Plus } from "lucide-react";
import * as React from "react";
import { Badge } from "../atoms/badge.js";
import { cx } from "../internal/cx.js";

export type QuickActionCardTone = "default" | "primary" | "success" | "warning";
export type QuickActionCardVariant = "action" | "add";
export type QuickActionCardSize = "sm" | "md";

export interface QuickActionCardProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "title"> {
  /** Leading icon (lucide). */
  icon?: React.ReactNode;
  /** Headline noun phrase. */
  title: React.ReactNode;
  /** Supporting copy. */
  description?: React.ReactNode;
  /**
   * Optional label pill next to the title (e.g. "Beta", "New").
   * Plain strings/numbers render as a `<Badge variant="on-ink">`.
   * Pass a custom node (including your own `<Badge>`) for full control.
   */
  badge?: React.ReactNode;
  /** Show or hide the trailing chevron. Defaults to `true` for `action`, `false` for `add`. */
  showChevron?: boolean;
  /** Visual treatment. `add` replaces the retired `AddTile` molecule. */
  variant?: QuickActionCardVariant;
  /** Size for the `add` variant. */
  size?: QuickActionCardSize;
  /** Color tone of the leading icon (default / primary → primary orange). */
  tone?: QuickActionCardTone;
  /** Render as the immediate child element (e.g. router Link). */
  asChild?: boolean;
}

/**
 * Clickable card that performs a single action. Used on dashboards and empty
 * states ("Add an employee", "Import from CSV") to guide the user to the
 * next obvious step. The whole card is the click target. Renders as a
 * `<button>` by default; pass `asChild` to swap in a router-aware Link.
 */
export const QuickActionCard = React.forwardRef<HTMLButtonElement, QuickActionCardProps>(
  function QuickActionCard(
    {
      asChild,
      badge,
      children,
      className,
      description,
      icon,
      showChevron,
      size = "md",
      title,
      tone = "default",
      type = "button",
      variant = "action",
      ...props
    },
    ref,
  ) {
    const Comp: React.ElementType = asChild ? Slot : "button";
    const buttonProps = asChild ? {} : { type };
    const renderedIcon = icon ?? (variant === "add" ? <Plus size={24} /> : null);
    const resolvedShowChevron = showChevron ?? variant === "action";

    const badgeNode = (() => {
      if (badge == null || badge === false) return null;
      if (typeof badge === "string" || typeof badge === "number") {
        return <Badge label={String(badge)} size="xs" variant="on-ink" />;
      }
      return badge;
    })();

    return (
      <Comp
        ref={ref}
        className={cx(
          "cw-v2-quick-action-card",
          `cw-v2-quick-action-card--variant-${variant}`,
          `cw-v2-quick-action-card--size-${size}`,
          `cw-v2-quick-action-card--${tone}`,
          "cw-v2-animate-quick-action-card-intro",
          className,
        )}
        {...buttonProps}
        {...props}
      >
        {resolvedShowChevron ? (
          <ChevronRight aria-hidden className="cw-v2-quick-action-card__chevron" />
        ) : null}
        {renderedIcon ? (
          <span aria-hidden className="cw-v2-quick-action-card__icon">
            {renderedIcon}
          </span>
        ) : null}
        <span className="cw-v2-quick-action-card__body">
          <span className="cw-v2-quick-action-card__title-row">
            <span className="cw-v2-quick-action-card__title">{title}</span>
            {badgeNode}
          </span>
          {description ? (
            <span className="cw-v2-quick-action-card__description">{description}</span>
          ) : null}
          {children}
        </span>
      </Comp>
    );
  },
);
QuickActionCard.displayName = "QuickActionCard";
